import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { useBusinessSettings, useUpdateBusinessSettings } from "@/hooks/useBusinessSettings";
import { useServiceTypes, useCreateServiceType, useDeleteServiceType } from "@/hooks/useBudgets";
import { MercadoPagoStatusWidget } from "./MercadoPagoStatusWidget";
import { formatTaxId, isValidTaxId, taxIdKind } from "@/lib/taxId";
import { toast } from "sonner";

function slugify(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

const sectionCls =
  "bg-card border border-border rounded-2xl shadow-card p-[26px] flex flex-col gap-[18px]";
const labelCls = "text-xs font-semibold text-muted-foreground";
const inputCls = "h-10 rounded-[10px] bg-muted/60 text-[13.5px] px-[13px]";
const primaryBtn = "h-[38px] px-[18px] rounded-[10px] text-[13px] font-semibold";
const outlineBtn = "h-[38px] px-4 rounded-[10px] text-[13px] font-semibold";

export function BillingSettingsTab() {
  const { data: settings, isLoading: settingsLoading } = useBusinessSettings();
  const updateSettings = useUpdateBusinessSettings();

  const [legalName, setLegalName] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [pixKey, setPixKey] = useState("");
  const [maxInstallments, setMaxInstallments] = useState(3);
  const [defaultRate, setDefaultRate] = useState(80);
  const [barterLimit, setBarterLimit] = useState(0);
  const [barterPeriod, setBarterPeriod] = useState<"month" | "year">("year");

  const resetFromSettings = () => {
    if (!settings) return;
    setLegalName(settings.legal_name ?? "");
    setCnpj(formatTaxId(settings.cnpj ?? ""));
    setPixKey(settings.pix_key ?? "");
    setMaxInstallments(settings.max_installments ?? 3);
    setDefaultRate(settings.default_hourly_rate);
    setBarterLimit(settings.barter_limit_amount ?? 0);
    setBarterPeriod(settings.barter_limit_period === "month" ? "month" : "year");
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(resetFromSettings, [settings?.id]);

  const { data: serviceTypes = [], isLoading: typesLoading } = useServiceTypes();
  const createType = useCreateServiceType();
  const deleteType = useDeleteServiceType();

  const [newTypeName, setNewTypeName] = useState("");
  const [newTypeRate, setNewTypeRate] = useState(80);
  const [newTypeHours, setNewTypeHours] = useState(10);

  const taxIdInvalid = !isValidTaxId(cnpj);

  const handleSaveBilling = () => {
    if (taxIdInvalid) {
      toast.error("CNPJ ou CPF inválido");
      return;
    }
    updateSettings.mutate({
      legal_name: legalName.trim() || null,
      cnpj: cnpj.trim() || null,
      pix_key: pixKey.trim() || null,
      max_installments: Math.max(1, Math.min(24, Math.round(maxInstallments) || 1)),
      barter_limit_amount: Math.max(0, Number(barterLimit) || 0),
      barter_limit_period: barterPeriod,
    });
  };

  const handleSaveRate = () => {
    updateSettings.mutate({ default_hourly_rate: defaultRate });
  };

  const handleCreateType = () => {
    if (!newTypeName.trim()) return;
    createType.mutate(
      {
        name: newTypeName.trim(),
        slug: slugify(newTypeName),
        // hours_min feeds the item prefill in the proposal editor (BudgetItemsEditor).
        hours_min: Math.max(0, Math.round(newTypeHours)),
        hours_max: Math.max(0, Math.round(newTypeHours)),
        default_hourly_rate: newTypeRate,
      },
      {
        onSuccess: () => {
          setNewTypeName("");
          setNewTypeRate(80);
          setNewTypeHours(10);
        },
      },
    );
  };

  const busy = updateSettings.isPending || settingsLoading;

  return (
    <div className="flex flex-col gap-4">
      <section className={sectionCls}>
        <h2 className="m-0 text-[15px] font-bold">Dados de cobrança</h2>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-[18px]">
          <div className="flex flex-col gap-[7px]">
            <label htmlFor="billing-legal-name" className={labelCls}>
              Razão social
            </label>
            <Input
              id="billing-legal-name"
              className={inputCls}
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              placeholder="Eliveuton Souza Tecnologia ME"
              disabled={settingsLoading}
            />
          </div>
          <div className="flex flex-col gap-[7px]">
            <label className={labelCls}>
              CNPJ ou CPF
              {taxIdKind(cnpj) && (
                <span className="ml-1.5 text-[11px] font-normal uppercase text-primary">
                  {taxIdKind(cnpj)}
                </span>
              )}
            </label>
            <Input
              className={`${inputCls} ${taxIdInvalid ? "border-bad focus-visible:ring-bad" : ""}`}
              value={cnpj}
              onChange={(e) => setCnpj(formatTaxId(e.target.value))}
              inputMode="numeric"
              placeholder="00.000.000/0001-00 ou 000.000.000-00"
              disabled={settingsLoading}
            />
            {taxIdInvalid && (
              <span className="text-[11.5px] text-bad">
                Documento inválido — confira os dígitos.
              </span>
            )}
          </div>
          <div className="flex flex-col gap-[7px]">
            <label htmlFor="billing-pix-key" className={labelCls}>
              Chave PIX padrão
            </label>
            <Input
              id="billing-pix-key"
              className={inputCls}
              value={pixKey}
              onChange={(e) => setPixKey(e.target.value)}
              placeholder="e-mail, CNPJ ou chave aleatória"
              disabled={settingsLoading}
            />
          </div>
          <div className="flex flex-col gap-[7px]">
            <label htmlFor="billing-max-installments" className={labelCls}>
              Parcelamento máximo
            </label>
            <div className="relative">
              <Input
                id="billing-max-installments"
                className={`${inputCls} pr-24`}
                type="number"
                min={1}
                max={24}
                value={maxInstallments}
                onChange={(e) => setMaxInstallments(Number(e.target.value))}
                disabled={settingsLoading}
              />
              <span className="pointer-events-none absolute right-[13px] top-1/2 -translate-y-1/2 text-[13.5px] text-muted-foreground">
                x no cartão
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-[7px]">
            <label htmlFor="billing-barter-limit" className={labelCls}>
              Teto de permuta
            </label>
            <div className="relative">
              <Input
                id="billing-barter-limit"
                className={`${inputCls} pl-10`}
                type="number"
                min={0}
                step={100}
                value={barterLimit}
                onChange={(e) => setBarterLimit(Number(e.target.value))}
                disabled={settingsLoading}
                aria-label="Teto de permuta em reais"
              />
              <span className="pointer-events-none absolute left-[13px] top-1/2 -translate-y-1/2 text-[13.5px] text-muted-foreground">
                R$
              </span>
            </div>
            <span className="text-[11.5px] text-muted-foreground">
              Quanto você aceita fechar em troca de serviços. 0 = sem teto.
            </span>
          </div>
          <div className="flex flex-col gap-[7px]">
            <label htmlFor="billing-barter-period" className={labelCls}>
              Período do teto
            </label>
            <Select
              value={barterPeriod}
              onValueChange={(v) => setBarterPeriod(v === "month" ? "month" : "year")}
              disabled={settingsLoading}
            >
              <SelectTrigger
                id="billing-barter-period"
                className={inputCls}
                aria-label="Período do teto de permuta"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Por mês</SelectItem>
                <SelectItem value="year">Por ano</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end gap-[9px]">
          <Button
            variant="outline"
            className={outlineBtn}
            onClick={resetFromSettings}
            disabled={busy}
          >
            Cancelar
          </Button>
          <Button className={primaryBtn} onClick={handleSaveBilling} disabled={busy}>
            {updateSettings.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar alterações
          </Button>
        </div>
      </section>

      <section className={sectionCls}>
        <h2 className="m-0 text-[15px] font-bold">Valor/hora padrão</h2>
        <p className="m-0 text-[12.5px] leading-relaxed text-muted-foreground">
          Usado como valor inicial ao criar um novo orçamento por hora, sem tipo de serviço
          selecionado.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex w-full max-w-[240px] flex-col gap-[7px]">
            <label htmlFor="billing-hourly-rate" className={labelCls}>
              Valor/hora (R$)
            </label>
            <Input
              id="billing-hourly-rate"
              className={inputCls}
              type="number"
              min={0}
              value={defaultRate}
              onChange={(e) => setDefaultRate(Number(e.target.value))}
              disabled={settingsLoading}
            />
          </div>
          <Button className={primaryBtn} onClick={handleSaveRate} disabled={busy}>
            {updateSettings.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
        <div className="border-b border-border/70 px-6 pb-3.5 pt-[18px]">
          <h2 className="m-0 text-[15px] font-bold">Tipos de serviço</h2>
          <p className="m-0 mt-0.5 text-[11.5px] text-muted-foreground">
            Cada tipo tem horas estimadas e valor/hora padrão — viram a linha inicial ao adicioná-lo
            a uma proposta.
          </p>
        </div>

        {typesLoading ? (
          <div className="py-6 text-center text-sm text-muted-foreground">Carregando...</div>
        ) : serviceTypes.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Nenhum tipo de serviço cadastrado.
          </div>
        ) : (
          serviceTypes.map((st) => (
            <div
              key={st.id}
              className="flex items-center gap-4 border-b border-border/70 px-6 py-3.5 hover:bg-muted/40"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13.5px] font-semibold">{st.name}</div>
                <div className="text-[11.5px] text-muted-foreground">
                  {st.hours_min > 0 ? `${st.hours_min}h · ` : ""}R${" "}
                  {Number(st.default_hourly_rate).toFixed(2)}/h
                </div>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-bad"
                    aria-label="Remover tipo"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remover tipo de serviço?</AlertDialogTitle>
                    <AlertDialogDescription>
                      "{st.name}" será removido. Orçamentos já criados com esse tipo não são
                      afetados.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => deleteType.mutate(st.id)}
                      className="bg-bad text-bad-foreground hover:bg-bad/90"
                    >
                      Remover
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))
        )}

        <div className="flex items-end gap-2 px-6 py-4">
          <div className="flex flex-1 flex-col gap-[7px]">
            <label htmlFor="service-type-name" className={labelCls}>
              Nome
            </label>
            <Input
              id="service-type-name"
              className={inputCls}
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder="Ex: Landing Page"
            />
          </div>
          <div className="flex w-24 flex-col gap-[7px]">
            <label htmlFor="service-type-hours" className={labelCls}>
              Horas est.
            </label>
            <Input
              id="service-type-hours"
              className={inputCls}
              type="number"
              min={0}
              value={newTypeHours}
              onChange={(e) => setNewTypeHours(Number(e.target.value))}
              aria-label="Horas estimadas do tipo de serviço"
            />
          </div>
          <div className="flex w-28 flex-col gap-[7px]">
            <label htmlFor="service-type-rate" className={labelCls}>
              Valor/hora
            </label>
            <Input
              id="service-type-rate"
              className={inputCls}
              type="number"
              min={0}
              value={newTypeRate}
              onChange={(e) => setNewTypeRate(Number(e.target.value))}
            />
          </div>
          <Button
            className="h-10 w-10 rounded-[10px] p-0"
            onClick={handleCreateType}
            disabled={createType.isPending || !newTypeName.trim()}
            aria-label="Adicionar tipo"
          >
            {createType.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
          </Button>
        </div>
      </section>

      <MercadoPagoStatusWidget />
    </div>
  );
}
